import { useState, useEffect } from 'react';
import { OpenAiApi } from 'api/OpenAi/OpenAi';
import { mindupMicsApi } from 'api/MindUp/mindUpMicsApi';

interface OpenAIInitData {
    diagnosisInfo: any;
    voucher: any;
    token: string;
    analysisResult?: string;
    testRawData?: string;
}

const INITIALIZATION_STATUS = {
    INITIALIZING: '초기화 중...',
    INSUFFICIENT_DATA: '필요한 데이터가 부족합니다',
    VERIFYING_ACCESS: 'AI 액세스 검증 중...',
    CREATING_INSTANCE: 'OpenAI 인스턴스 생성 중...',
    INITIALIZING_ASSISTANT: 'Assistant 초기화 중...',
    CREATING_THREAD: '대화 스레드 생성 중...',
    COMPLETED: '초기화 완료',
};

export const useOpenAI = ({ diagnosisInfo, voucher, token, analysisResult, testRawData }: OpenAIInitData) => {
    const [openAiInstance, setOpenAiInstance] = useState<OpenAiApi | null>(null);
    const [threadId, setThreadId] = useState<string | null>(null);
    const [initializationStatus, setInitializationStatus] = useState<string>(INITIALIZATION_STATUS.INITIALIZING);

    useEffect(() => {
        const initializeOpenAI = async () => {
            console.log('🚀 OpenAI 초기화 시작...', { diagnosisInfo, voucher, token: !!token });

            if (!diagnosisInfo || !voucher || !token) {
                console.log('❌ 필요한 데이터가 없습니다:', {
                    diagnosisInfo: !!diagnosisInfo,
                    voucher: !!voucher,
                    token: !!token,
                });
                setInitializationStatus(INITIALIZATION_STATUS.INSUFFICIENT_DATA);
                return;
            }

            try {
                setInitializationStatus(INITIALIZATION_STATUS.VERIFYING_ACCESS);
                const voucherCode = voucher?.data?.voucher?.code || voucher?.code;
                const aiResponse = await mindupMicsApi.verifyAiAccess(voucherCode, diagnosisInfo._id, token);
                // console.log('✅ AI 액세스 검증 완료:', aiResponse.data);

                setInitializationStatus(INITIALIZATION_STATUS.CREATING_INSTANCE);
                // console.log('🤖 OpenAI API 인스턴스 생성 중...');
                const openAiApiInstance = new OpenAiApi(aiResponse.data.apiKey);

                setInitializationStatus(INITIALIZATION_STATUS.INITIALIZING_ASSISTANT);
                // console.log('🎯 Assistant 초기화 중...');
                await openAiApiInstance.initAssistant(aiResponse.data.model);
                // console.log('✅ Assistant 초기화 완료');

                setInitializationStatus(INITIALIZATION_STATUS.CREATING_THREAD);
                // console.log('🧵 Chat Thread 생성 중...');
                const thread = await openAiApiInstance.createChatThread(analysisResult);
                // console.log('✅ Chat Thread 생성 완료:', thread.id);
                // console.log('📊 분석 결과 컨텍스트:', analysisResult ? '포함됨' : '없음');

                setThreadId(thread.id);
                setOpenAiInstance(openAiApiInstance);
                setInitializationStatus(INITIALIZATION_STATUS.COMPLETED);
                // console.log('🎉 OpenAI 초기화 완료!');
            } catch (error) {
                console.error('❌ OpenAI 초기화 실패:', error);
                setInitializationStatus('초기화 실패: ' + (error as Error).message);
            }
        };

        initializeOpenAI();
    }, [diagnosisInfo, voucher, token, analysisResult, testRawData]);

    const sendMessage = async (message: string): Promise<string> => {
        if (!openAiInstance || !threadId) {
            throw new Error('OpenAI가 초기화되지 않았습니다.');
        }

        return await openAiInstance.sendChatMessage(testRawData || '', threadId, message);
    };

    return {
        openAiInstance,
        threadId,
        initializationStatus,
        isReady: !!openAiInstance && !!threadId,
        sendMessage,
    };
};
